import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FiArrowLeft,
  FiChevronDown,
  FiChevronRight,
  FiCheckSquare,
  FiEdit2,
  FiFileText,
  FiFolder,
  FiKey,
  FiLock,
  FiMoreVertical,
  FiPlus,
  FiSquare,
  FiTrash2,
  FiUnlock,
  FiX,
} from 'react-icons/fi';
import { createCurriculumService } from './curriculumService';
import APMiniInputModal from './CurriculumMiniInputModal';
import './Curriculum.css';

function makeLocalId() {
  return `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function createEmptyBatchFileItem() {
  return { id: makeLocalId(), folderName: '', title: '', file: null, answerKeyFile: null, answerKeyLink: '' };
}

function createEmptyBatchLinkItem() {
  return { id: makeLocalId(), folderName: '', title: '', linkUrl: '', answerKeyFile: null, answerKeyLink: '' };
}

function normalizeRowCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 1;
  return Math.max(1, Math.min(500, Math.floor(n)));
}

function ensureBatchFileRows(targetCount, currentRows) {
  const count = normalizeRowCount(targetCount);
  const base = Array.isArray(currentRows) ? currentRows : [];
  return Array.from({ length: count }, (_, i) => {
    if (base[i]) return { ...base[i] };
    return createEmptyBatchFileItem();
  });
}

function ensureBatchLinkRows(targetCount, currentRows) {
  const count = normalizeRowCount(targetCount);
  const base = Array.isArray(currentRows) ? currentRows : [];
  return Array.from({ length: count }, (_, i) => {
    if (base[i]) return { ...base[i] };
    return createEmptyBatchLinkItem();
  });
}

function parsePastedGrid(rawText) {
  if (!rawText) return [];
  const lines = rawText
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n');

  while (lines.length > 0 && lines[lines.length - 1] === '') {
    lines.pop();
  }

  return lines.map((line) => line.split('\t'));
}

function ResourceEditorModal({
  modalState,
  onClose,
  onSubmit,
  submitting,
  onChangeField,
  onSetBatchFileRowCount,
  onSetBatchLinkRowCount,
  onUpdateBatchFileRow,
  onUpdateBatchLinkRow,
  onPasteBatchFileGrid,
  onPasteBatchLinkGrid,
  onAppendBatchFiles,
  onAppendBatchAnswerKeyFiles,
}) {
  const isEdit = modalState.intent === 'edit-resource';
  const allowTabs = modalState.intent === 'add-resource';
  const activeTab = allowTabs ? modalState.tab : 'single';
  const isBatchTab = activeTab === 'batch-file' || activeTab === 'batch-link';
  const submitLabel = activeTab === 'single' ? 'Save' : 'Save All';
  const selectedBatchOptions = [
    modalState.attachFolders ? 'Folder' : null,
    modalState.attachAnswerKeys ? 'Answer Key' : null,
  ].filter(Boolean);
  const optionsSummaryText =
    selectedBatchOptions.length > 0 ? selectedBatchOptions.join(', ') : 'Columns & Options';

  return (
    <div className="apcModal__overlay" role="dialog" aria-modal="true">
      <div className={`apcModal__card ${isBatchTab ? 'apcModal__card--wide' : ''}`}>
        <div className="apcModal__header">
          <h3>{modalState.headerTitle}</h3>
          <button type="button" className="apcIconBtn" onClick={onClose}>
            <FiX />
          </button>
        </div>

        <div className="apcModal__body">
          {allowTabs && (
            <div className="apcTabs">
              <button
                type="button"
                className={`apcTab ${activeTab === 'single' ? 'is-active' : ''}`}
                onClick={() => onChangeField('tab', 'single')}
              >
                Single
              </button>
              <button
                type="button"
                className={`apcTab ${activeTab === 'batch-file' ? 'is-active' : ''}`}
                onClick={() => onChangeField('tab', 'batch-file')}
              >
                Batch Files
              </button>
              <button
                type="button"
                className={`apcTab ${activeTab === 'batch-link' ? 'is-active' : ''}`}
                onClick={() => onChangeField('tab', 'batch-link')}
              >
                Batch Links
              </button>
            </div>
          )}

          {activeTab === 'single' && (
            <>
              <label className="apcField">
                <span>Title</span>
                <input
                  value={modalState.title}
                  onChange={(e) => onChangeField('title', e.target.value)}
                  placeholder="Resource title"
                />
              </label>

              <label className="apcField">
                <span>Source</span>
                <select value={modalState.mode} onChange={(e) => onChangeField('mode', e.target.value)}>
                  {isEdit && <option value="keep">Keep Existing File/Link</option>}
                  <option value="file">Upload File</option>
                  <option value="link">Paste Link</option>
                </select>
              </label>

              {modalState.mode === 'file' && (
                <label className="apcField">
                  <span>{isEdit ? 'Replace File' : 'File'}</span>
                  <input type="file" onChange={(e) => onChangeField('file', e.target.files?.[0] || null)} />
                </label>
              )}

              {modalState.mode === 'link' && (
                <label className="apcField">
                  <span>Link URL</span>
                  <input
                    value={modalState.linkUrl}
                    onChange={(e) => onChangeField('linkUrl', e.target.value)}
                    placeholder="https://..."
                  />
                </label>
              )}
            </>
          )}

          {activeTab === 'batch-file' && (
            <div className="apcBatchSection">
              <div className="apcBatchTop">
                <div className="apcBatchTopLeft apcBatchTopLeft--wrap">
                  <label className="apcField apcField--rows">
                    <span>Rows</span>
                    <input
                      type="number"
                      min={1}
                      value={modalState.batchFiles.length}
                      onChange={(e) => onSetBatchFileRowCount(e.target.value)}
                    />
                  </label>

                  <details className="apcMultiSelect">
                    <summary className="apcBtn apcBtn--ghost apcMultiSelect__trigger">
                      {optionsSummaryText}
                    </summary>
                    <div className="apcMultiSelect__menu">
                      <label className="apcCheckField">
                        <input
                          type="checkbox"
                          checked={!!modalState.attachFolders}
                          onChange={(e) => onChangeField('attachFolders', e.target.checked)}
                        />
                        <span>Attach Folders</span>
                      </label>
                      <label className="apcCheckField">
                        <input
                          type="checkbox"
                          checked={!!modalState.attachAnswerKeys}
                          onChange={(e) => onChangeField('attachAnswerKeys', e.target.checked)}
                        />
                        <span>Attach Answer Keys</span>
                      </label>
                      {modalState.attachAnswerKeys && (
                        <label className="apcField apcField--compact">
                          <span>Answer Key Source</span>
                          <select value={modalState.answerKeyMode} onChange={(e) => onChangeField('answerKeyMode', e.target.value)}>
                            <option value="file">Upload File</option>
                            <option value="link">Paste Link</option>
                          </select>
                        </label>
                      )}
                    </div>
                  </details>

                  <label className="apcBtn apcBtn--ghost apcBtn--filePick">
                    Select Multiple Files
                    <input
                      type="file"
                      multiple
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        if (files.length > 0) onAppendBatchFiles(files);
                        e.target.value = '';
                      }}
                    />
                  </label>

                  {modalState.attachAnswerKeys && modalState.answerKeyMode === 'file' && (
                    <label className="apcBtn apcBtn--ghost apcBtn--filePick">
                      Select Multiple Answer Keys
                      <input
                        type="file"
                        multiple
                        onChange={(e) => {
                          const files = Array.from(e.target.files || []);
                          if (files.length > 0) onAppendBatchAnswerKeyFiles('batch-file', files);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  )}
                </div>
              </div>

              <div className="apcTableWrap">
                <table className="apcTable">
                  <thead>
                    <tr>
                      <th>#</th>
                      {modalState.attachFolders && <th>Folder</th>}
                      <th>Title</th>
                      <th>File</th>
                      {modalState.attachAnswerKeys && <th>{modalState.answerKeyMode === 'file' ? 'Answer Key File' : 'Answer Key Link'}</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {modalState.batchFiles.map((item, index) => (
                      <tr key={item.id}>
                        <td>{index + 1}</td>
                        {modalState.attachFolders && (
                          <td>
                            <input
                              value={item.folderName || ''}
                              onChange={(e) => {
                                const updater = activeTab === 'batch-file' ? onUpdateBatchFileRow : onUpdateBatchLinkRow;
                                updater(item.id, { folderName: e.target.value });
                              }}
                              onPaste={(e) => {
                                const text = e.clipboardData.getData('text');
                                if (!text || !text.includes('\n')) return;
                                e.preventDefault();
                                const paster = activeTab === 'batch-file' ? onPasteBatchFileGrid : onPasteBatchLinkGrid;
                                paster(index, 'folderName', text);
                              }}
                              placeholder="Folder Name"
                            />
                          </td>
                        )}
                        <td>
                          <input
                            value={item.title}
                            onChange={(e) => onUpdateBatchFileRow(item.id, { title: e.target.value })}
                            onPaste={(e) => {
                              const text = e.clipboardData.getData('text');
                              if (!text || !text.includes('\n')) return;
                              e.preventDefault();
                              onPasteBatchFileGrid(index, 'title', text);
                            }}
                            placeholder="Resource title"
                          />
                        </td>
                        <td>
                          <input
                            type="file"
                            onChange={(e) => onUpdateBatchFileRow(item.id, { file: e.target.files?.[0] || null })}
                            ref={(node) => {
                              if (node) {
                                if (item.file) {
                                  // Update the DOM input only if the file doesn't already match
                                  if (node.files.length === 0 || node.files[0] !== item.file) {
                                    const dt = new DataTransfer();
                                    dt.items.add(item.file);
                                    node.files = dt.files;
                                  }
                                } else {
                                  // Clear the native input if the state is cleared
                                  node.value = '';
                                }
                              }
                            }}
                          />
                        </td>
                        {modalState.attachAnswerKeys && (
                          <td>
                            {modalState.answerKeyMode === 'file' ? (
                              <input
                                type="file"
                                onChange={(e) => onUpdateBatchFileRow(item.id, { answerKeyFile: e.target.files?.[0] || null })}
                                ref={(node) => {
                                  if (node) {
                                    if (item.answerKeyFile) {
                                      if (node.files.length === 0 || node.files[0] !== item.answerKeyFile) {
                                        const dt = new DataTransfer();
                                        dt.items.add(item.answerKeyFile);
                                        node.files = dt.files;
                                      }
                                    } else {
                                      node.value = '';
                                    }
                                  }
                                }}
                              />
                            ) : (
                              <input
                                value={item.answerKeyLink}
                                onChange={(e) => onUpdateBatchFileRow(item.id, { answerKeyLink: e.target.value })}
                                placeholder="https://..."
                              />
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'batch-link' && (
            <div className="apcBatchSection">
              <div className="apcBatchTop">
                <div className="apcBatchTopLeft apcBatchTopLeft--wrap">
                  <label className="apcField apcField--rows">
                    <span>Rows</span>
                    <input
                      type="number"
                      min={1}
                      value={modalState.batchLinks.length}
                      onChange={(e) => onSetBatchLinkRowCount(e.target.value)}
                    />
                  </label>

                  <details className="apcMultiSelect">
                    <summary className="apcBtn apcBtn--ghost apcMultiSelect__trigger">
                      {optionsSummaryText}
                    </summary>
                    <div className="apcMultiSelect__menu">
                      <label className="apcCheckField">
                        <input
                          type="checkbox"
                          checked={!!modalState.attachFolders}
                          onChange={(e) => onChangeField('attachFolders', e.target.checked)}
                        />
                        <span>Attach Folders</span>
                      </label>
                      <label className="apcCheckField">
                        <input
                          type="checkbox"
                          checked={!!modalState.attachAnswerKeys}
                          onChange={(e) => onChangeField('attachAnswerKeys', e.target.checked)}
                        />
                        <span>Attach Answer Keys</span>
                      </label>
                      {modalState.attachAnswerKeys && (
                        <label className="apcField apcField--compact">
                          <span>Answer Key Source</span>
                          <select value={modalState.answerKeyMode} onChange={(e) => onChangeField('answerKeyMode', e.target.value)}>
                            <option value="file">Upload File</option>
                            <option value="link">Paste Link</option>
                          </select>
                        </label>
                      )}
                    </div>
                  </details>

                  {modalState.attachAnswerKeys && modalState.answerKeyMode === 'file' && (
                    <label className="apcBtn apcBtn--ghost apcBtn--filePick">
                      Select Multiple Answer Keys
                      <input
                        type="file"
                        multiple
                        onChange={(e) => {
                          const files = Array.from(e.target.files || []);
                          if (files.length > 0) onAppendBatchAnswerKeyFiles('batch-link', files);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  )}
                </div>
              </div>

              <div className="apcTableWrap">
                <table className="apcTable">
                  <thead>
                    <tr>
                      <th>#</th>
                      {modalState.attachFolders && <th>Folder</th>}
                      <th>Title</th>
                      <th>Link URL</th>
                      {modalState.attachAnswerKeys && <th>{modalState.answerKeyMode === 'file' ? 'Answer Key File' : 'Answer Key Link'}</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {modalState.batchLinks.map((item, index) => (
                      <tr key={item.id}>
                        <td>{index + 1}</td>
                        {modalState.attachFolders && (
                          <td>
                            <input
                              value={item.folderName || ''}
                              onChange={(e) => onUpdateBatchLinkRow(item.id, { folderName: e.target.value })}
                              onPaste={(e) => {
                                const text = e.clipboardData.getData('text');
                                if (!text || !text.includes('\n')) return;
                                e.preventDefault();
                                onPasteBatchLinkGrid(index, 'folderName', text);
                              }}
                              placeholder="Folder Name"
                            />
                          </td>
                        )}
                        <td>
                          <input
                            value={item.title}
                            onChange={(e) => onUpdateBatchLinkRow(item.id, { title: e.target.value })}
                            onPaste={(e) => {
                              const text = e.clipboardData.getData('text');
                              if (!text || !text.includes('\n')) return;
                              e.preventDefault();
                              onPasteBatchLinkGrid(index, 'title', text);
                            }}
                            placeholder="Resource title"
                          />
                        </td>
                        <td>
                          <input
                            value={item.linkUrl}
                            onChange={(e) => onUpdateBatchLinkRow(item.id, { linkUrl: e.target.value })}
                            onPaste={(e) => {
                              const text = e.clipboardData.getData('text');
                              if (!text || !text.includes('\n')) return;
                              e.preventDefault();
                              onPasteBatchLinkGrid(index, 'linkUrl', text);
                            }}
                            placeholder="https://..."
                          />
                        </td>
                        {modalState.attachAnswerKeys && (
                          <td>
                            {modalState.answerKeyMode === 'file' ? (
                              <input
                                type="file"
                                onChange={(e) => onUpdateBatchLinkRow(item.id, { answerKeyFile: e.target.files?.[0] || null })}
                                ref={(node) => {
                                  if (node) {
                                    if (item.answerKeyFile) {
                                      if (node.files.length === 0 || node.files[0] !== item.answerKeyFile) {
                                        const dt = new DataTransfer();
                                        dt.items.add(item.answerKeyFile);
                                        node.files = dt.files;
                                      }
                                    } else {
                                      node.value = '';
                                    }
                                  }
                                }}
                              />
                            ) : (
                              <input
                                value={item.answerKeyLink}
                                onChange={(e) => onUpdateBatchLinkRow(item.id, { answerKeyLink: e.target.value })}
                                onPaste={(e) => {
                                  const text = e.clipboardData.getData('text');
                                  if (!text || !text.includes('\n')) return;
                                  e.preventDefault();
                                  onPasteBatchLinkGrid(index, 'answerKeyLink', text);
                                }}
                                placeholder="https://..."
                              />
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="apcModal__footer">
          <button type="button" className="apcBtn apcBtn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="apcBtn apcBtn--primary" onClick={onSubmit} disabled={submitting}>
            {submitting ? 'Saving...' : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function MoveToFolderModal({
  folders,
  folderId,
  onChangeFolderId,
  onClose,
  onSubmit,
  submitting,
  selectedCount,
}) {
  return (
    <div className="apcModal__overlay" role="dialog" aria-modal="true">
      <div className="apcModal__card apcModal__card--mini">
        <div className="apcModal__header">
          <h3>Move To Folder</h3>
          <button type="button" className="apcIconBtn" onClick={onClose}>
            <FiX />
          </button>
        </div>

        <div className="apcModal__body">
          <div className="apcd__moveHint">
            {selectedCount} selected resource{selectedCount === 1 ? '' : 's'} will be moved.
          </div>
          <label className="apcField">
            <span>Folder</span>
            <select value={folderId} onChange={(e) => onChangeFolderId(e.target.value)}>
              <option value="">Select folder...</option>
              {folders.map((folder) => (
                <option key={folder.id} value={folder.id}>
                  {folder.title}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="apcModal__footer">
          <button type="button" className="apcBtn apcBtn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="apcBtn apcBtn--primary" onClick={onSubmit} disabled={submitting || !folderId}>
            {submitting ? 'Moving...' : 'Move'}
          </button>
        </div>
      </div>
    </div>
  );
}

function openResource(resource) {
  if (!resource?.url) return;
  window.open(resource.url, '_blank', 'noopener,noreferrer');
}

export default function CurriculumTextbookDetail({
  library,
  categoryId,
  textbookId,
  onBack,
  isEditMode,
  onToggleEditMode,
  canToggleEditMode = true,
  canDelete = true,
  onLibraryChanged,
}) {
  // Bind every data operation to this library. Methods are aliased to their
  // legacy AP names so the (large) body below stays untouched.
  const service = useMemo(() => createCurriculumService(library), [library]);
  const {
    createResource: createAPResource,
    updateResource: updateAPResource,
    deleteResource: deleteAPResource,
    deleteResourcesBulk: deleteAPResourcesBulk,
    fetchTextbookDetail: fetchAPTextbookDetail,
    moveResourcesToFolder: moveAPResourcesToFolder,
    reorderResources: reorderAPResources,
  } = service;

  const [loading, setLoading] = useState(true);
  const [textbook, setTextbook] = useState(null);
  const [resources, setResources] = useState([]);
  const [answerKeysByParent, setAnswerKeysByParent] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [resourceModal, setResourceModal] = useState(null);
  const [separatorModal, setSeparatorModal] = useState(null);
  const [folderModal, setFolderModal] = useState(null);
  const [moveToFolderModal, setMoveToFolderModal] = useState(null);
  const [selectedResourceIds, setSelectedResourceIds] = useState({});
  const [dragState, setDragState] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState({});
  const [uploadProgress, setUploadProgress] = useState({ show: false, current: 0, total: 0, done: false });

  const loadDetail = useCallback(async () => {
    setLoading(true);
    try {
      const detail = await fetchAPTextbookDetail(categoryId, textbookId);
      setTextbook(detail.textbook);
      setResources(detail.resources);
      setAnswerKeysByParent(detail.answerKeysByParent || {});
    } catch (error) {
      alert(error.message || 'Failed to load textbook.');
    } finally {
      setLoading(false);
    }
  }, [fetchAPTextbookDetail, categoryId, textbookId]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  useEffect(() => {
    setSelectedResourceIds({});
    setDragState(null);
  }, [resources, answerKeysByParent]);

  useEffect(() => {
    const folders = resources.filter((item) => item.type === 'folder');
    setExpandedFolders((prev) => {
      const next = {};
      folders.forEach((folder) => {
        next[folder.id] = prev[folder.id] ?? false;
      });
      return next;
    });
  }, [resources]);

  const allSelectableResourceIds = useMemo(() => {
    const ids = [];
    resources.forEach((resource) => {
      ids.push(resource.id);
      const keys = answerKeysByParent[resource.id] || [];
      keys.forEach((keyItem) => ids.push(keyItem.id));
    });
    return ids;
  }, [resources, answerKeysByParent]);

  const selectedCount = useMemo(
    () => allSelectableResourceIds.filter((id) => selectedResourceIds[id]).length,
    [allSelectableResourceIds, selectedResourceIds]
  );

  const resourceById = useMemo(() => {
    const map = {};
    resources.forEach((item) => {
      map[item.id] = item;
    });
    return map;
  }, [resources]);

  const folders = useMemo(
    () => resources.filter((item) => item.type === 'folder'),
    [resources]
  );

  const folderById = useMemo(() => {
    const map = {};
    folders.forEach((folder) => {
      map[folder.id] = folder;
    });
    return map;
  }, [folders]);

  const childrenByFolderId = useMemo(() => {
    const map = {};
    resources.forEach((item) => {
      if (!item.folderId || !folderById[item.folderId]) return;
      if (!map[item.folderId]) map[item.folderId] = [];
      map[item.folderId].push(item);
    });
    return map;
  }, [resources, folderById]);

  const topLevelResources = useMemo(
    () => resources.filter((item) => !item.folderId || !folderById[item.folderId]),
    [resources, folderById]
  );

  const selectedMovableResourceIds = useMemo(
    () =>
      Object.keys(selectedResourceIds).filter((id) => {
        if (!selectedResourceIds[id]) return false;
        const resource = resourceById[id];
        if (!resource) return false;
        return resource.type !== 'separator' && resource.type !== 'folder';
      }),
    [selectedResourceIds, resourceById]
  );

  const openAddResourceModal = () => {
    setResourceModal({
      intent: 'add-resource',
      headerTitle: 'Add Resource',
      resource: null,
      title: '',
      mode: 'file',
      linkUrl: '',
      file: null,
      isAnswerKey: false,
      parentId: '',
      folderId: '',
      tab: 'batch-file',
      attachAnswerKeys: false,
      answerKeyMode: 'file',
      attachFolders: true,
      batchFiles: ensureBatchFileRows(10, []),
      batchLinks: ensureBatchLinkRows(10, []),
    });
  };

  const openAddSeparatorModal = () => {
    setSeparatorModal({
      mode: 'add',
      title: 'Add Separator',
      label: 'Separator Title',
      value: '',
      submitLabel: 'Create',
      resource: null,
    });
  };

  const openEditSeparatorModal = (resource) => {
    setSeparatorModal({
      mode: 'edit',
      title: 'Rename Separator',
      label: 'Separator Title',
      value: resource.title || '',
      submitLabel: 'Save',
      resource,
    });
  };

  const closeSeparatorModal = () => setSeparatorModal(null);

  const openAddFolderModal = () => {
    setFolderModal({
      mode: 'add',
      title: 'Add Folder',
      label: 'Folder Name',
      value: '',
      submitLabel: 'Create',
      resource: null,
    });
  };

  const openEditFolderModal = (resource) => {
    setFolderModal({
      mode: 'edit',
      title: 'Rename Folder',
      label: 'Folder Name',
      value: resource.title || '',
      submitLabel: 'Save',
      resource,
    });
  };

  const closeFolderModal = () => setFolderModal(null);

  const openAddAnswerKeyModal = (parentResource) => {
    setResourceModal({
      intent: 'add-answer-key',
      headerTitle: 'Attach Answer Key',
      resource: null,
      title: parentResource.title ? `${parentResource.title} - Answer Key` : 'Answer Key',
      mode: 'file',
      linkUrl: '',
      file: null,
      isAnswerKey: true,
      parentId: parentResource.id,
      folderId: '',
      tab: 'single',
      attachAnswerKeys: false,
      answerKeyMode: 'file',
      batchFiles: ensureBatchFileRows(1, []),
      batchLinks: ensureBatchLinkRows(1, []),
    });
  };

  const openEditResourceModal = (resource, parentId = '') => {
    setResourceModal({
      intent: 'edit-resource',
      headerTitle: resource.isAnswerKey ? 'Edit Answer Key' : 'Edit Resource',
      resource,
      title: resource.title || '',
      mode: 'keep',
      linkUrl: resource.type === 'link' ? resource.url || '' : '',
      file: null,
      isAnswerKey: !!resource.isAnswerKey,
      parentId: parentId || resource.parentId || '',
      folderId: resource.folderId || '',
      tab: 'single',
      attachAnswerKeys: false,
      answerKeyMode: 'file',
      attachFolders: false,
      batchFiles: ensureBatchFileRows(1, []),
      batchLinks: ensureBatchLinkRows(1, []),
    });
  };

  const closeResourceModal = () => setResourceModal(null);

  const submitResourceModal = async () => {
    if (!resourceModal) return;

    try {
      setSubmitting(true);

      const allowTabs = resourceModal.intent === 'add-resource';
      const activeTab = allowTabs ? resourceModal.tab : 'single';

      if (activeTab === 'batch-file') {
        const rows = resourceModal.batchFiles.filter((row) => row.title.trim() || row.file);
        if (rows.length === 0) {
          alert('Please add at least one row.');
          return;
        }

        const invalid = rows.some((row) => !row.title.trim() || !row.file);
        if (invalid) {
          alert('Please fill title and file for every non-empty row.');
          return;
        }

        const hasBatchAnswerKeys = !!resourceModal.attachAnswerKeys;
        const answerKeyMode = resourceModal.answerKeyMode === 'link' ? 'link' : 'file';
        const answerKeyTotal = hasBatchAnswerKeys
          ? rows.filter((row) => (answerKeyMode === 'file' ? !!row.answerKeyFile : !!row.answerKeyLink?.trim())).length
          : 0;

        // Close the main modal and show progress UI
        closeResourceModal(); 
        setUploadProgress({ show: true, current: 0, total: rows.length + answerKeyTotal, done: false });

        try {
          let completed = 0;
          const folderCache = {}; // Tracks newly created folders so we don't duplicate them
          for (let i = 0; i < rows.length; i += 1) {
            const row = rows[i];
            
            let currentFolderId = '';
            const fName = (row.folderName || '').trim();
            if (fName) {
              const existingFolder = folders.find((f) => f.title.toLowerCase() === fName.toLowerCase());
              if (existingFolder) {
                currentFolderId = existingFolder.id;
              } else if (folderCache[fName.toLowerCase()]) {
                currentFolderId = folderCache[fName.toLowerCase()];
              } else {
                currentFolderId = await createAPResource({
                  categoryId,
                  textbookId, title: fName, mode: 'folder', linkUrl: '', file: null, isAnswerKey: false, folderId: '', parentId: '', orderIndex: resources.length + Object.keys(folderCache).length
                });
                folderCache[fName.toLowerCase()] = currentFolderId;
              }
            }

            const resourceId = await createAPResource({
              categoryId,
              textbookId,
              title: row.title.trim(),
              mode: 'file',
              linkUrl: '',
              file: row.file,
              isAnswerKey: false,
              folderId: currentFolderId,
              parentId: '',
              orderIndex: resources.length + i + Object.keys(folderCache).length,
            });
            completed += 1;
            setUploadProgress({ show: true, current: completed, total: rows.length + answerKeyTotal, done: false });

            if (hasBatchAnswerKeys) {
              const keyFile = row.answerKeyFile || null;
              const keyLink = (row.answerKeyLink || '').trim();
              const hasAnswerKey = answerKeyMode === 'file' ? !!keyFile : !!keyLink;

              if (hasAnswerKey) {
                await createAPResource({
                  categoryId,
                  textbookId,
                  title: row.title?.trim() ? `${row.title.trim()} - Answer Key` : 'Answer Key',
                  mode: answerKeyMode,
                  linkUrl: keyLink,
                  file: keyFile,
                  isAnswerKey: true,
                  parentId: resourceId,
                  orderIndex: 0,
                });
                completed += 1;
                setUploadProgress({ show: true, current: completed, total: rows.length + answerKeyTotal, done: false });
              }
            }
          }

          // Mark as done and refresh background data
          setUploadProgress((prev) => ({ ...prev, done: true }));
          await loadDetail();
          onLibraryChanged?.();
        } catch (err) {
          alert(err.message || 'Failed during batch upload.');
          setUploadProgress({ show: false, current: 0, total: 0, done: false });
        } finally {
          setSubmitting(false);
        }
        return; // Exit early to bypass the default close/reload at the bottom of the function
      } else if (activeTab === 'batch-link') {
        const rows = resourceModal.batchLinks.filter((row) => row.title.trim() || row.linkUrl.trim());
        if (rows.length === 0) {
          alert('Please add at least one row.');
          return;
        }

        const invalid = rows.some((row) => !row.title.trim() || !row.linkUrl.trim());
        if (invalid) {
          alert('Please fill title and link for every non-empty row.');
          return;
        }

        const hasBatchAnswerKeys = !!resourceModal.attachAnswerKeys;
        const answerKeyMode = resourceModal.answerKeyMode === 'link' ? 'link' : 'file';

        const folderCache = {};
        for (let i = 0; i < rows.length; i += 1) {
          const row = rows[i];
          
          let currentFolderId = '';
          const fName = (row.folderName || '').trim();
          if (fName) {
            const existingFolder = folders.find((f) => f.title.toLowerCase() === fName.toLowerCase());
            if (existingFolder) {
              currentFolderId = existingFolder.id;
            } else if (folderCache[fName.toLowerCase()]) {
              currentFolderId = folderCache[fName.toLowerCase()];
            } else {
              currentFolderId = await createAPResource({
                categoryId,
                textbookId, title: fName, mode: 'folder', linkUrl: '', file: null, isAnswerKey: false, folderId: '', parentId: '', orderIndex: resources.length + Object.keys(folderCache).length
              });
              folderCache[fName.toLowerCase()] = currentFolderId;
            }
          }

          const resourceId = await createAPResource({
            categoryId,
            textbookId,
            title: row.title.trim(),
            mode: 'link',
            linkUrl: row.linkUrl.trim(),
            file: null,
            isAnswerKey: false,
            folderId: currentFolderId,
            parentId: '',
            orderIndex: resources.length + i + Object.keys(folderCache).length,
          });

          if (hasBatchAnswerKeys) {
            const keyFile = row.answerKeyFile || null;
            const keyLink = (row.answerKeyLink || '').trim();
            const hasAnswerKey = answerKeyMode === 'file' ? !!keyFile : !!keyLink;

            if (hasAnswerKey) {
              await createAPResource({
                categoryId,
                textbookId,
                title: row.title?.trim() ? `${row.title.trim()} - Answer Key` : 'Answer Key',
                mode: answerKeyMode,
                linkUrl: keyLink,
                file: keyFile,
                isAnswerKey: true,
                parentId: resourceId,
                orderIndex: 0,
              });
            }
          }
        }
      } else {
        const title = resourceModal.title?.trim();
        if (!title) {
          alert('Please enter a title.');
          return;
        }

        if (resourceModal.mode === 'link' && !resourceModal.linkUrl?.trim()) {
          alert('Please enter a URL.');
          return;
        }

        if (!resourceModal.resource && resourceModal.mode === 'file' && !resourceModal.file) {
          alert('Please choose a file.');
          return;
        }

        if (resourceModal.resource && resourceModal.mode === 'file' && !resourceModal.file) {
          alert('Please choose a replacement file.');
          return;
        }

        if (!resourceModal.resource) {
          const parentKey = resourceModal.parentId || '';
          const orderIndex = resourceModal.isAnswerKey
            ? (answerKeysByParent[parentKey] || []).length
            : resources.length;

          await createAPResource({
            categoryId,
            textbookId,
            title,
            mode: resourceModal.mode,
            linkUrl: resourceModal.linkUrl,
            file: resourceModal.file,
            isAnswerKey: resourceModal.isAnswerKey,
            folderId: resourceModal.folderId || '',
            parentId: resourceModal.parentId || '',
            orderIndex,
          });
        } else {
          await updateAPResource({
            categoryId,
            textbookId,
            resourceId: resourceModal.resource.id,
            title,
            mode: resourceModal.mode,
            linkUrl: resourceModal.linkUrl,
            file: resourceModal.file,
            isAnswerKey: resourceModal.isAnswerKey,
            folderId: resourceModal.folderId || '',
            parentId: resourceModal.parentId || '',
          });
        }
      }

      closeResourceModal();
      await loadDetail();
      onLibraryChanged?.();
    } catch (error) {
      alert(error.message || 'Failed to save resource.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitSeparatorModal = async () => {
    if (!separatorModal) return;
    const title = separatorModal.value?.trim();
    if (!title) return;

    try {
      setSubmitting(true);
      if (separatorModal.mode === 'add') {
        await createAPResource({
          categoryId,
          textbookId,
          title,
          mode: 'separator',
          linkUrl: '',
          file: null,
          isAnswerKey: false,
          folderId: '',
          parentId: '',
          orderIndex: resources.length,
        });
      } else if (separatorModal.resource) {
        await updateAPResource({
          categoryId,
          textbookId,
          resourceId: separatorModal.resource.id,
          title,
          mode: 'separator',
          linkUrl: '',
          file: null,
          isAnswerKey: false,
          folderId: '',
          parentId: '',
        });
      }

      closeSeparatorModal();
      await loadDetail();
      onLibraryChanged?.();
    } catch (error) {
      alert(error.message || 'Failed to save separator.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitFolderModal = async () => {
    if (!folderModal) return;
    const title = folderModal.value?.trim();
    if (!title) return;

    try {
      setSubmitting(true);
      if (folderModal.mode === 'add') {
        await createAPResource({
          categoryId,
          textbookId,
          title,
          mode: 'folder',
          linkUrl: '',
          file: null,
          isAnswerKey: false,
          folderId: '',
          parentId: '',
          orderIndex: resources.length,
        });
      } else if (folderModal.resource) {
        await updateAPResource({
          categoryId,
          textbookId,
          resourceId: folderModal.resource.id,
          title,
          mode: 'folder',
          linkUrl: '',
          file: null,
          isAnswerKey: false,
          folderId: '',
          parentId: '',
        });
      }

      closeFolderModal();
      await loadDetail();
      onLibraryChanged?.();
    } catch (error) {
      alert(error.message || 'Failed to save folder.');
    } finally {
      setSubmitting(false);
    }
  };

  const openMoveSelectedToFolderModal = () => {
    if (selectedMovableResourceIds.length === 0) {
      alert('Select at least one resource to move.');
      return;
    }
    if (folders.length === 0) {
      alert('Create a folder first.');
      return;
    }
    setMoveToFolderModal({ folderId: '' });
  };

  const closeMoveToFolderModal = () => setMoveToFolderModal(null);

  const submitMoveToFolderModal = async () => {
    if (!moveToFolderModal?.folderId) {
      alert('Please choose a folder.');
      return;
    }
    if (selectedMovableResourceIds.length === 0) {
      closeMoveToFolderModal();
      return;
    }

    const folderId = moveToFolderModal.folderId;
    const movingFolderIds = new Set(
      selectedMovableResourceIds.filter((id) => resourceById[id]?.type === 'folder')
    );

    if (movingFolderIds.has(folderId)) {
      alert('Cannot move a folder into itself.');
      return;
    }

    const movableIds = selectedMovableResourceIds.filter((id) => {
      const resource = resourceById[id];
      if (!resource) return false;
      if (resource.type === 'folder') return false;
      if (resource.id === folderId) return false;
      return true;
    });

    if (movableIds.length === 0) {
      alert('No movable resources selected.');
      return;
    }

    try {
      setSubmitting(true);
      await moveAPResourcesToFolder({
        categoryId,
        textbookId,
        resourceIds: movableIds,
        folderId,
      });
      setSelectedResourceIds({});
      setExpandedFolders((prev) => ({ ...prev, [folderId]: true }));
      closeMoveToFolderModal();
      await loadDetail();
      onLibraryChanged?.();
    } catch (error) {
      alert(error.message || 'Failed to move resources.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteResource = async (resource) => {
    if (!window.confirm(`Delete "${resource.title}"?`)) return;

    try {
      setSubmitting(true);
      await deleteAPResource({ categoryId, textbookId, resourceId: resource.id });
      await loadDetail();
      onLibraryChanged?.();
    } catch (error) {
      alert(error.message || 'Failed to delete resource.');
    } finally {
      setSubmitting(false);
    }
  };

  const setResourceSelection = (resourceId, checked) => {
    setSelectedResourceIds((prev) => {
      const next = { ...prev };
      const childKeys = answerKeysByParent[resourceId] || []; // Grab any attached answer keys

      if (checked) {
        next[resourceId] = true;
        // Auto-select all attached answer keys
        childKeys.forEach(keyItem => {
          next[keyItem.id] = true;
        });
      } else {
        delete next[resourceId];
        // Auto-deselect all attached answer keys
        childKeys.forEach(keyItem => {
          delete next[keyItem.id];
        });
      }
      return next;
    });
  };

  const handleToggleSelectAll = () => {
    const allSelected = allSelectableResourceIds.length > 0 && allSelectableResourceIds.every((id) => selectedResourceIds[id]);

    if (allSelected) {
      setSelectedResourceIds({});
      return;
    }

    const next = {};
    allSelectableResourceIds.forEach((id) => {
      next[id] = true;
    });
    setSelectedResourceIds(next);
  };

  const handleDeleteSelectedResources = async () => {
    const ids = allSelectableResourceIds.filter((id) => selectedResourceIds[id]);
    if (ids.length === 0) return;

    const ok = window.confirm(`Delete ${ids.length} selected item(s)? This will also remove linked answer keys.`);
    if (!ok) return;

    try {
      setSubmitting(true);
      await deleteAPResourcesBulk({
        categoryId,
        textbookId,
        resourceIds: ids,
      });
      setSelectedResourceIds({});
      await loadDetail();
      onLibraryChanged?.();
    } catch (error) {
      alert(error.message || 'Failed to delete selected resources.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReorderResources = async (items, fromIndex, toIndex) => {
    try {
      if (fromIndex === toIndex) return;
      setSubmitting(true);
      await reorderAPResources({
        categoryId,
        textbookId,
        resources: items,
        fromIndex,
        toIndex,
      });
      await loadDetail();
      onLibraryChanged?.();
    } catch (error) {
      alert(error.message || 'Failed to move resource.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTopRowDrop = async (items, toIndex) => {
    if (!dragState || dragState.scope !== 'top') return;
    const fromIndex = dragState.index;
    setDragState(null);
    await handleReorderResources(items, fromIndex, toIndex);
  };

  const handleAnswerKeyRowDrop = async (parentId, keys, toIndex) => {
    if (!dragState || dragState.scope !== 'answer' || dragState.parentId !== parentId) return;
    const fromIndex = dragState.index;
    setDragState(null);
    await handleReorderResources(keys, fromIndex, toIndex);
  };

  const handleFolderRowDrop = async (folderId, items, toIndex) => {
    if (!dragState || dragState.scope !== 'folder' || dragState.folderId !== folderId) return;
    const fromIndex = dragState.index;
    setDragState(null);
    await handleReorderResources(items, fromIndex, toIndex);
  };

  const allSelected =
    allSelectableResourceIds.length > 0 && allSelectableResourceIds.every((id) => selectedResourceIds[id]);

  if (loading) {
    return <div className="apcLoading">Loading AP textbook...</div>;
  }

  if (!textbook) {
    return (
      <div className="apcLoading">
        <button type="button" className="apcBtn apcBtn--ghost" onClick={onBack}>
          Back
        </button>
        <div>Textbook not found.</div>
      </div>
    );
  }

  return (
    <div className={`apcd ${canDelete ? '' : 'apcd--noDelete'}`}>
      <div className="apcd__topbar">
        <button type="button" className="apcBtn apcBtn--ghost" onClick={onBack}>
          <FiArrowLeft /> Back
        </button>

        {canToggleEditMode && (
          <button
            type="button"
            className="apcIconBtn apcIconBtn--lock"
            onClick={onToggleEditMode}
            title={isEditMode ? 'Lock Edit Mode' : 'Unlock Edit Mode'}
          >
            {isEditMode ? <FiUnlock /> : <FiLock />}
          </button>
        )}
      </div>

      <div className="apcd__layout">
        <aside className="apcd__leftPane">
          <div className="apcd__coverWrap">
            {textbook.coverImageUrl ? (
              <img src={textbook.coverImageUrl} alt={textbook.title} className="apcd__cover" />
            ) : (
              <div className="apcd__coverPlaceholder">No Cover</div>
            )}
          </div>
          <h2 className="apcd__title">{textbook.title}</h2>
        </aside>

        <section className="apcd__rightPane">
          <div className="apcd__resourcesHeader">
            <h3>Resources</h3>
            {isEditMode && (
              <div className="apcd__headerActions">
                <button type="button" className="apcBtn apcBtn--primary" onClick={openAddResourceModal} disabled={submitting}>
                  <FiPlus /> Add Resource
                </button>
                <button type="button" className="apcBtn apcBtn--ghost" onClick={openAddSeparatorModal} disabled={submitting}>
                  <FiPlus /> Add Separator
                </button>
                <button type="button" className="apcBtn apcBtn--ghost" onClick={openAddFolderModal} disabled={submitting}>
                  <FiPlus /> Add Folder
                </button>
                <button type="button" className="apcBtn apcBtn--ghost" onClick={handleToggleSelectAll} disabled={submitting}>
                  {allSelected ? (
                    <>
                      <FiSquare /> Clear Selection
                    </>
                  ) : (
                    <>
                      <FiCheckSquare /> Select All
                    </>
                  )}
                </button>
                <button
                  type="button"
                  className="apcBtn apcBtn--ghost"
                  onClick={openMoveSelectedToFolderModal}
                  disabled={submitting || selectedMovableResourceIds.length === 0 || folders.length === 0}
                >
                  <FiFolder /> Move Selected To Folder
                </button>
                <button
                  type="button"
                  className="apcBtn apcBtn--ghost apcBtn--dangerOutline"
                  onClick={handleDeleteSelectedResources}
                  disabled={submitting || selectedCount === 0}
                >
                  <FiTrash2 /> Delete Selected ({selectedCount})
                </button>
              </div>
            )}
          </div>

          <div className="apcd__resourcesCard">
            <div className="apcd__resourceList">
              {topLevelResources.map((resource, resourceIndex) => {
                const keys = answerKeysByParent[resource.id] || [];
                const isSeparator = resource.type === 'separator';
                const isFolder = resource.type === 'folder';
                const folderChildren = childrenByFolderId[resource.id] || [];
                const isFolderExpanded = !!expandedFolders[resource.id];
                const isDraggingTop = dragState?.scope === 'top' && dragState.index === resourceIndex;

                return (
                  <div key={resource.id} className="apcd__resourceGroup">
                    <div
                      className={`apcd__resourceRow ${isEditMode ? 'apcd__resourceRow--editable' : ''} ${isDraggingTop ? 'is-dragging' : ''}`}
                      draggable={isEditMode}
                      onDragStart={() => setDragState({ scope: 'top', index: resourceIndex })}
                      onDragEnd={() => setDragState(null)}
                      onDragOver={(e) => {
                        if (!isEditMode) return;
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                      }}
                      onDrop={(e) => {
                        if (!isEditMode) return;
                        e.preventDefault();
                        handleTopRowDrop(topLevelResources, resourceIndex);
                      }}
                    >
                      {isEditMode && (
                        <>
                          <label className="apcd__checkCell">
                            <input
                              type="checkbox"
                              checked={!!selectedResourceIds[resource.id]}
                              onChange={(e) => setResourceSelection(resource.id, e.target.checked)}
                            />
                          </label>
                          <span className="apcd__dragHandle" title="Drag to reorder">
                            <FiMoreVertical />
                          </span>
                        </>
                      )}

                      {isSeparator ? (
                        <div className="apcd__separatorMain">
                          <div className="apcd__separatorTitle">{resource.title}</div>
                          <div className="apcd__separatorLine" />
                        </div>
                      ) : isFolder ? (
                        <button
                          type="button"
                          className="apcd__folderToggle"
                          onClick={() => setExpandedFolders((prev) => ({ ...prev, [resource.id]: !prev[resource.id] }))}
                          title={isFolderExpanded ? 'Collapse Folder' : 'Expand Folder'}
                        >
                          {isFolderExpanded ? <FiChevronDown /> : <FiChevronRight />}
                          <FiFolder />
                          <span className="apcd__folderTitle">{resource.title}</span>
                          <span className="apcd__folderCount">{folderChildren.length}</span>
                        </button>
                      ) : (
                        <button type="button" className="apcd__resourceBtn" onClick={() => openResource(resource)}>
                          <FiFileText />
                          <span>{resource.title}</span>
                        </button>
                      )}

                      {!isEditMode && !isSeparator && !isFolder && keys.length > 0 && (
                        <div className="apcd__answerPills">
                          {keys.map((keyItem) => (
                            <button
                              key={keyItem.id}
                              type="button"
                              className="apcd__answerTag apcd__answerTag--clickable"
                              onClick={() => openResource(keyItem)}
                              title="Open Answer Key"
                            >
                              <FiKey /> Answer Key
                            </button>
                          ))}
                        </div>
                      )}

                      {isEditMode && (
                        <div className="apcd__actions">
                          {isSeparator ? (
                            <>
                              <button type="button" className="apcIconBtn" onClick={() => openEditSeparatorModal(resource)}>
                                <FiEdit2 />
                              </button>
                              <button type="button" className="apcIconBtn apcIconBtn--danger" onClick={() => handleDeleteResource(resource)}>
                                <FiTrash2 />
                              </button>
                            </>
                          ) : isFolder ? (
                            <>
                              <button type="button" className="apcIconBtn" onClick={() => openEditFolderModal(resource)}>
                                <FiEdit2 />
                              </button>
                              <button type="button" className="apcIconBtn apcIconBtn--danger" onClick={() => handleDeleteResource(resource)}>
                                <FiTrash2 />
                              </button>
                            </>
                          ) : (
                            <>
                              <button type="button" className="apcIconBtn" onClick={() => openEditResourceModal(resource)}>
                                <FiEdit2 />
                              </button>
                              <button type="button" className="apcIconBtn" onClick={() => openAddAnswerKeyModal(resource)} title="Attach Answer Key">
                                <FiPlus />
                              </button>
                              <button type="button" className="apcIconBtn apcIconBtn--danger" onClick={() => handleDeleteResource(resource)}>
                                <FiTrash2 />
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    {isEditMode &&
                      !isFolder &&
                      keys.map((keyItem, keyIndex) => {
                        const isDraggingAnswer =
                          dragState?.scope === 'answer' &&
                          dragState.parentId === resource.id &&
                          dragState.index === keyIndex;

                        return (
                          <div
                            key={keyItem.id}
                            className={`apcd__childRow apcd__childRow--editable ${isDraggingAnswer ? 'is-dragging' : ''}`}
                            draggable={isEditMode}
                            onDragStart={() => setDragState({ scope: 'answer', parentId: resource.id, index: keyIndex })}
                            onDragEnd={() => setDragState(null)}
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.dataTransfer.dropEffect = 'move';
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              handleAnswerKeyRowDrop(resource.id, keys, keyIndex);
                            }}
                          >
                            <label className="apcd__checkCell">
                              <input
                                type="checkbox"
                                checked={!!selectedResourceIds[keyItem.id]}
                                onChange={(e) => setResourceSelection(keyItem.id, e.target.checked)}
                              />
                            </label>
                            <span className="apcd__dragHandle" title="Drag to reorder">
                              <FiMoreVertical />
                            </span>

                            <span className="apcd__answerTag">
                              <FiKey /> Answer Key
                            </span>

                            <button type="button" className="apcd__resourceBtn apcd__resourceBtn--child" onClick={() => openResource(keyItem)}>
                              <FiFileText />
                              <span>{keyItem.title}</span>
                            </button>

                            <div className="apcd__actions">
                              <button type="button" className="apcIconBtn" onClick={() => openEditResourceModal(keyItem, resource.id)}>
                                <FiEdit2 />
                              </button>
                              <button type="button" className="apcIconBtn apcIconBtn--danger" onClick={() => handleDeleteResource(keyItem)}>
                                <FiTrash2 />
                              </button>
                            </div>
                          </div>
                        );
                      })}

                    {isFolder && isFolderExpanded && (
                      <div className="apcd__folderChildren">
                        {folderChildren.map((childResource, childIndex) => {
                          const childKeys = answerKeysByParent[childResource.id] || [];
                          const isDraggingFolderChild =
                            dragState?.scope === 'folder' &&
                            dragState.folderId === resource.id &&
                            dragState.index === childIndex;

                          return (
                            <div key={childResource.id} className="apcd__resourceGroup">
                              <div
                                className={`apcd__resourceRow apcd__resourceRow--child ${isEditMode ? 'apcd__resourceRow--editable' : ''} ${isDraggingFolderChild ? 'is-dragging' : ''}`}
                                draggable={isEditMode}
                                onDragStart={() => setDragState({ scope: 'folder', folderId: resource.id, index: childIndex })}
                                onDragEnd={() => setDragState(null)}
                                onDragOver={(e) => {
                                  if (!isEditMode) return;
                                  e.preventDefault();
                                  e.dataTransfer.dropEffect = 'move';
                                }}
                                onDrop={(e) => {
                                  if (!isEditMode) return;
                                  e.preventDefault();
                                  handleFolderRowDrop(resource.id, folderChildren, childIndex);
                                }}
                              >
                                {isEditMode && (
                                  <>
                                    <label className="apcd__checkCell">
                                      <input
                                        type="checkbox"
                                        checked={!!selectedResourceIds[childResource.id]}
                                        onChange={(e) => setResourceSelection(childResource.id, e.target.checked)}
                                      />
                                    </label>
                                    <span className="apcd__dragHandle" title="Drag to reorder">
                                      <FiMoreVertical />
                                    </span>
                                  </>
                                )}

                                <button type="button" className="apcd__resourceBtn" onClick={() => openResource(childResource)}>
                                  <FiFileText />
                                  <span>{childResource.title}</span>
                                </button>

                                {!isEditMode && childKeys.length > 0 && (
                                  <div className="apcd__answerPills">
                                    {childKeys.map((keyItem) => (
                                      <button
                                        key={keyItem.id}
                                        type="button"
                                        className="apcd__answerTag apcd__answerTag--clickable"
                                        onClick={() => openResource(keyItem)}
                                        title="Open Answer Key"
                                      >
                                        <FiKey /> Answer Key
                                      </button>
                                    ))}
                                  </div>
                                )}

                                {isEditMode && (
                                  <div className="apcd__actions">
                                    <button type="button" className="apcIconBtn" onClick={() => openEditResourceModal(childResource)}>
                                      <FiEdit2 />
                                    </button>
                                    <button type="button" className="apcIconBtn" onClick={() => openAddAnswerKeyModal(childResource)} title="Attach Answer Key">
                                      <FiPlus />
                                    </button>
                                    <button type="button" className="apcIconBtn apcIconBtn--danger" onClick={() => handleDeleteResource(childResource)}>
                                      <FiTrash2 />
                                    </button>
                                  </div>
                                )}
                              </div>

                              {isEditMode &&
                                childKeys.map((keyItem, keyIndex) => {
                                  const isDraggingAnswer =
                                    dragState?.scope === 'answer' &&
                                    dragState.parentId === childResource.id &&
                                    dragState.index === keyIndex;

                                  return (
                                    <div
                                      key={keyItem.id}
                                      className={`apcd__childRow apcd__childRow--editable ${isDraggingAnswer ? 'is-dragging' : ''}`}
                                      draggable={isEditMode}
                                      onDragStart={() => setDragState({ scope: 'answer', parentId: childResource.id, index: keyIndex })}
                                      onDragEnd={() => setDragState(null)}
                                      onDragOver={(e) => {
                                        e.preventDefault();
                                        e.dataTransfer.dropEffect = 'move';
                                      }}
                                      onDrop={(e) => {
                                        e.preventDefault();
                                        handleAnswerKeyRowDrop(childResource.id, childKeys, keyIndex);
                                      }}
                                    >
                                      <label className="apcd__checkCell">
                                        <input
                                          type="checkbox"
                                          checked={!!selectedResourceIds[keyItem.id]}
                                          onChange={(e) => setResourceSelection(keyItem.id, e.target.checked)}
                                        />
                                      </label>
                                      <span className="apcd__dragHandle" title="Drag to reorder">
                                        <FiMoreVertical />
                                      </span>

                                      <span className="apcd__answerTag">
                                        <FiKey /> Answer Key
                                      </span>

                                      <button type="button" className="apcd__resourceBtn apcd__resourceBtn--child" onClick={() => openResource(keyItem)}>
                                        <FiFileText />
                                        <span>{keyItem.title}</span>
                                      </button>

                                      <div className="apcd__actions">
                                        <button type="button" className="apcIconBtn" onClick={() => openEditResourceModal(keyItem, childResource.id)}>
                                          <FiEdit2 />
                                        </button>
                                        <button type="button" className="apcIconBtn apcIconBtn--danger" onClick={() => handleDeleteResource(keyItem)}>
                                          <FiTrash2 />
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              {resources.length === 0 && (
                <div className="apcd__empty">
                  {canToggleEditMode ? 'No resources yet. Unlock edit mode to add one.' : 'No resources yet.'}
                </div>
              )}
            </div>
          </div>
        </section>
      </div>

      {resourceModal && (
        <ResourceEditorModal
          modalState={resourceModal}
          onClose={closeResourceModal}
          onSubmit={submitResourceModal}
          submitting={submitting}
          onChangeField={(field, value) => setResourceModal((prev) => ({ ...prev, [field]: value }))}
          onSetBatchFileRowCount={(count) =>
            setResourceModal((prev) => ({ ...prev, batchFiles: ensureBatchFileRows(count, prev.batchFiles) }))
          }
          onSetBatchLinkRowCount={(count) =>
            setResourceModal((prev) => ({ ...prev, batchLinks: ensureBatchLinkRows(count, prev.batchLinks) }))
          }
          onUpdateBatchFileRow={(rowId, patch) =>
            setResourceModal((prev) => ({
              ...prev,
              batchFiles: prev.batchFiles.map((row) => (row.id === rowId ? { ...row, ...patch } : row)),
            }))
          }
          onUpdateBatchLinkRow={(rowId, patch) =>
            setResourceModal((prev) => ({
              ...prev,
              batchLinks: prev.batchLinks.map((row) => (row.id === rowId ? { ...row, ...patch } : row)),
            }))
          }
          onPasteBatchFileGrid={(startRow, startCol, rawText) => {
            const grid = parsePastedGrid(rawText);
            if (grid.length === 0) return;
            setResourceModal((prev) => {
              let rows = ensureBatchFileRows(startRow + grid.length, prev.batchFiles);
              grid.forEach((cols, i) => {
                const rowIndex = startRow + i;
                const current = rows[rowIndex];
                if (startCol === 'folderName') {
                  rows[rowIndex] = {
                    ...current,
                    folderName: cols[0] ?? '',
                    title: cols[1] ?? current.title,
                  };
                } else {
                  rows[rowIndex] = { ...current, title: cols[0] ?? '' };
                }
              });
              return { ...prev, batchFiles: rows };
            });
          }}
          onAppendBatchFiles={(files) => {
            setResourceModal((prev) => {
              const incoming = Array.isArray(files) ? files : [];
              if (incoming.length === 0) return prev;

              let firstEmpty = prev.batchFiles.findIndex((row) => !row.file);
              if (firstEmpty < 0) firstEmpty = prev.batchFiles.length;

              const requiredRows = firstEmpty + incoming.length;
              const rows = ensureBatchFileRows(requiredRows, prev.batchFiles);

              incoming.forEach((file, index) => {
                const rowIndex = firstEmpty + index;
                rows[rowIndex] = { ...rows[rowIndex], file };
              });

              return { ...prev, batchFiles: rows };
            });
          }}
          onAppendBatchAnswerKeyFiles={(targetTab, files) => {
            setResourceModal((prev) => {
              const incoming = Array.isArray(files) ? files : [];
              if (incoming.length === 0) return prev;

              const field = targetTab === 'batch-link' ? 'batchLinks' : 'batchFiles';
              const baseRows = Array.isArray(prev[field]) ? prev[field] : [];
              let firstEmpty = baseRows.findIndex((row) => !row.answerKeyFile);
              if (firstEmpty < 0) firstEmpty = baseRows.length;

              const ensureRows =
                targetTab === 'batch-link' ? ensureBatchLinkRows : ensureBatchFileRows;
              const rows = ensureRows(firstEmpty + incoming.length, baseRows);

              incoming.forEach((file, index) => {
                const rowIndex = firstEmpty + index;
                rows[rowIndex] = { ...rows[rowIndex], answerKeyFile: file };
              });

              return { ...prev, [field]: rows };
            });
          }}
          onPasteBatchLinkGrid={(startRow, startCol, rawText) => {
            const grid = parsePastedGrid(rawText);
            if (grid.length === 0) return;
            setResourceModal((prev) => {
              let rows = ensureBatchLinkRows(startRow + grid.length, prev.batchLinks);
              grid.forEach((cols, i) => {
                const rowIndex = startRow + i;
                const current = rows[rowIndex];
                if (startCol === 'folderName') {
                  rows[rowIndex] = {
                    ...current,
                    folderName: cols[0] ?? '',
                    title: cols[1] ?? current.title,
                    linkUrl: cols.length > 2 ? cols[2] ?? current.linkUrl : current.linkUrl,
                  };
                } else if (startCol === 'title') {
                  rows[rowIndex] = {
                    ...current,
                    title: cols[0] ?? '',
                    linkUrl: cols.length > 1 ? cols[1] ?? current.linkUrl : current.linkUrl,
                  };
                } else if (startCol === 'answerKeyLink') {
                  rows[rowIndex] = { ...current, answerKeyLink: cols[0] ?? '' };
                } else {
                  rows[rowIndex] = { ...current, linkUrl: cols[0] ?? '' };
                }
              });
              return { ...prev, batchLinks: rows };
            });
          }}
        />
      )}

      {separatorModal && (
        <APMiniInputModal
          title={separatorModal.title}
          label={separatorModal.label}
          value={separatorModal.value}
          onChange={(value) => setSeparatorModal((prev) => ({ ...prev, value }))}
          onCancel={closeSeparatorModal}
          onSubmit={submitSeparatorModal}
          submitting={submitting}
          submitLabel={separatorModal.submitLabel}
        />
      )}

      {folderModal && (
        <APMiniInputModal
          title={folderModal.title}
          label={folderModal.label}
          value={folderModal.value}
          onChange={(value) => setFolderModal((prev) => ({ ...prev, value }))}
          onCancel={closeFolderModal}
          onSubmit={submitFolderModal}
          submitting={submitting}
          submitLabel={folderModal.submitLabel}
        />
      )}

      {moveToFolderModal && (
        <MoveToFolderModal
          folders={folders}
          folderId={moveToFolderModal.folderId}
          onChangeFolderId={(folderId) => setMoveToFolderModal((prev) => ({ ...prev, folderId }))}
          onClose={closeMoveToFolderModal}
          onSubmit={submitMoveToFolderModal}
          submitting={submitting}
          selectedCount={selectedMovableResourceIds.length}
        />
      )}

      {uploadProgress.show && (
        <div className="apcModal__overlay" style={{ zIndex: 1300 }}>
          <div className="apcModal__card apcModal__card--mini" style={{ padding: '24px', textAlign: 'center' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px' }}>
              {uploadProgress.done ? 'Upload Complete! 🎉' : 'Uploading Files...'}
            </h3>

            {/* Progress Bar Container */}
            <div style={{ background: '#e5e7eb', height: '14px', borderRadius: '7px', overflow: 'hidden', marginBottom: '12px' }}>
              {/* Progress Fill */}
              <div style={{
                background: uploadProgress.done ? '#10b981' : '#2b63d6',
                height: '100%',
                width: `${uploadProgress.total > 0 ? (uploadProgress.current / uploadProgress.total) * 100 : 0}%`,
                transition: 'width 0.3s ease, background 0.3s'
              }} />
            </div>

            <p style={{ margin: '0', fontSize: '14px', color: '#64748b', fontWeight: '600' }}>
              {uploadProgress.current} / {uploadProgress.total} files
            </p>

            {uploadProgress.done && (
              <button
                type="button"
                className="apcBtn apcBtn--primary"
                style={{ marginTop: '20px', width: '100%' }}
                onClick={() => setUploadProgress({ show: false, current: 0, total: 0, done: false })}
              >
                Done
              </button>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
